/**
 * Fixed-size ring buffer for diagnostic logging in offline subsystems.
 *
 * Captures tagged log lines (info, warn, error, sync) emitted by the
 * mutation-queue, media-queue, sync-manager, and auth refresh path.
 *
 * The buffer is in-memory and rolls past capacity (default 200).
 * It is dumped into the FieldSupportBundle when the user taps "Send to admin."
 *
 * Auth tokens and refresh tokens are redacted before storage.
 */

export type LogLevel = 'info' | 'warn' | 'error' | 'sync';

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: number;
}

const TOKEN_PATTERNS = [
  /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, // JWT
  /Bearer\s+[^\s"']+/gi,
];

function redactTokens(message: string): string {
  let result = message;
  for (const pattern of TOKEN_PATTERNS) {
    result = result.replace(pattern, '[REDACTED]');
  }
  return result;
}

class LogBuffer {
  private buffer: LogEntry[] = [];
  private capacity: number;

  constructor(capacity = 200) {
    this.capacity = capacity;
  }

  /** Record a log entry, redacting any auth tokens. */
  record(level: LogLevel, message: string): void {
    const entry: LogEntry = {
      level,
      message: redactTokens(message),
      timestamp: Date.now(),
    };
    this.buffer.push(entry);
    if (this.buffer.length > this.capacity) {
      this.buffer.shift();
    }
  }

  /** Convenience methods */
  info(message: string): void {
    this.record('info', message);
  }
  warn(message: string): void {
    this.record('warn', message);
  }
  error(message: string): void {
    this.record('error', message);
  }
  sync(message: string): void {
    this.record('sync', message);
  }

  /** Dump all entries (for diagnostic bundle). */
  dump(): LogEntry[] {
    return [...this.buffer];
  }

  /** Number of entries currently held. */
  get size(): number {
    return this.buffer.length;
  }

  /** Clear all entries. */
  clear(): void {
    this.buffer = [];
  }
}

/** Singleton log buffer shared by all offline subsystems. */
export const offlineLog = new LogBuffer(200);
